import { useState } from 'react'
import {
  type BillingCycle,
  BillingCycleSelector,
} from '@/features/billing/components/BillingCycleSelector'
import { UsageMetricsTable } from '@/features/billing/components/UsageMetricsTable'
import type { BillingOverview } from '@/features/billing/types/billing.types'
import { Skeleton } from '@/shared/components/ui/skeleton'

interface UsageTabProps {
  billingData: BillingOverview | null
}

export function UsageTab({ billingData }: UsageTabProps) {
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>('current')

  if (!billingData) {
    return (
      <div className='space-y-6'>
        {/* Header with Billing Cycle Selector Skeleton */}
        <div className='flex items-center justify-between'>
          <div className='w-64'>
            <Skeleton className='h-10 w-full' />
          </div>
        </div>

        {/* Usage Metrics Table Skeleton */}
        <div className='space-y-6'>
          {/* Events Usage Table Skeleton */}
          <div className='bg-transparent border rounded-lg p-6 space-y-4'>
            <Skeleton className='h-6 w-32' />

            {/* Table Header Skeleton */}
            <div className='grid grid-cols-8 gap-4 pb-4 border-b'>
              <Skeleton className='h-4 w-12' />
              {Array.from({ length: 7 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                <Skeleton key={i} className='h-4 w-16' />
              ))}
            </div>

            {/* Table Rows Skeleton */}
            {Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              <div key={i} className='grid grid-cols-8 gap-4 py-2'>
                <Skeleton className='h-4 w-20' />
                {Array.from({ length: 7 }).map((_, j) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                  <Skeleton key={j} className='h-4 w-12' />
                ))}
              </div>
            ))}
          </div>

          {/* Spend by Category Table Skeleton */}
          <div className='bg-transparent border rounded-lg p-6 space-y-4'>
            <Skeleton className='h-6 w-40' />

            {/* Table Header Skeleton */}
            <div className='grid grid-cols-3 gap-4 pb-4 border-b'>
              <Skeleton className='h-4 w-16' />
              <Skeleton className='h-4 w-20' />
              <Skeleton className='h-4 w-16' />
            </div>

            {/* Table Rows Skeleton */}
            {Array.from({ length: 4 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              <div key={i} className='grid grid-cols-3 gap-4 py-2'>
                <Skeleton className='h-4 w-24' />
                <Skeleton className='h-4 w-16' />
                <Skeleton className='h-4 w-12' />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div className='w-64'>
          <BillingCycleSelector
            value={selectedCycle}
            onValueChange={setSelectedCycle}
          />
        </div>
      </div>

      <UsageMetricsTable billingCycle={selectedCycle} />
    </div>
  )
}
